import {
	ENTRY_REASON_MAX,
	ENTRY_STATES,
	type EntryPhase,
	type EntryState,
	type EntrySubmission,
	type EntryWindowStatus,
} from "@seagames/contracts";
import { Modal, ModalActions } from "@seagames/ui";
import { useBlocker } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import { invalidateEntries, reopenEntryPhase } from "../../lib/api/entries";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { toast } from "../../lib/ui/toast";
import { LockIcon } from "../accreditation/icons";
import { leavesEditor } from "./model";
import type { StatusGuideGroup } from "../common/status-guide";
import { WindowStatusPill } from "../registration-windows/window-status";
import { AlertIcon, InfoIcon } from "../transport/icons";
import "./entries.css";

/**
 * Small pieces every Sport entries screen shares: the state pill, the window
 * line, the EOI → EBN → Shortlist strip, the banners, the Statuses guide, the
 * How it works sheet, the reopen sheet and the leave-without-saving guard.
 * Every sentence is a key in the `entries` namespace.
 */

const STATE_TONE: Record<EntryState, string> = {
	NOT_STARTED: "bg-gms-soft-2 text-gms-ink-3",
	OPEN: "bg-gms-navy-soft text-gms-navy",
	FINAL: "bg-gms-ok-soft text-gms-ok",
	REOPENED: "bg-gms-warn-soft text-gms-warn",
};

/** Not started · In progress · Submitted (lock) · Reopened. */
export function StatePill({ state }: { state: EntryState }) {
	const { t } = useTranslation("entries");
	return (
		<span
			className={`inline-flex h-[1.25rem] items-center gap-[0.25rem] rounded-[0.25rem] px-[0.4375rem] text-[0.65625rem] font-semibold whitespace-nowrap ${STATE_TONE[state]}`}
			title={t(`state.hint.${state}`)}
		>
			{state === "FINAL" && <LockIcon className="size-[0.75rem]" />}
			{t(`state.${state}`)}
		</span>
	);
}

/**
 * "Closes 23 Oct, 3:49PM" / "Opens …" / "Closed 19 Nov, 12:00AM" / "Closed"
 * — viewer-local, absolute; the zone in the tooltip. `short` (a table under
 * a "… closes" header) drops the verb of an open window: "23 Oct, 3:49PM".
 */
export function WindowLine({ window: w, className = "", short = false }: { window: EntryWindowStatus; className?: string; short?: boolean }) {
	const { t } = useTranslation("entries");
	if (w.open && w.closes_at)
		return (
			<span className={className} title={formatDateTime(w.closes_at)}>
				{short ? formatShort(w.closes_at) : t("window.closesAt", { at: formatShort(w.closes_at) })}
			</span>
		);
	if (w.next_opens_at)
		return (
			<span className={className} title={formatDateTime(w.next_opens_at)}>
				{t("window.opensAt", { at: formatShort(w.next_opens_at) })}
			</span>
		);
	// Closed: when, if the API names the window that ended (`closes_at` while not open).
	if (w.closes_at)
		return (
			<span className={className} title={formatDateTime(w.closes_at)}>
				{t("window.closedAt", { at: formatShort(w.closes_at) })}
			</span>
		);
	return <span className={className}>{t("window.closed")}</span>;
}

/** The window's state as the Settings page's pill (open / upcoming / closed). */
export function WindowPill({ window: w }: { window: EntryWindowStatus }) {
	return <WindowStatusPill state={w.open ? "open" : w.next_opens_at ? "upcoming" : "closed"} />;
}

/** "23 Oct, 3:49PM by Nurul" — when the phase was submitted (or reopened). */
export function SubmissionStamp({ submission }: { submission: EntrySubmission }) {
	const { t } = useTranslation("entries");
	const reopened = submission.state === "REOPENED";
	const at = reopened ? submission.reopened_at : submission.submitted_at;
	const by = reopened ? submission.reopened_by : submission.submitted_by;
	if (!at) return null;
	return <span title={formatDateTime(at)}>{by ? t("final.stampBy", { at: formatShort(at), name: by.name }) : t("final.stamp", { at: formatShort(at) })}</span>;
}

export interface StepInfo {
	state: EntryState;
	window: EntryWindowStatus;
}

/** A one-line notice above an editor: tone + icon + bold title + a line. */
export function Notice({ tone, title, children }: { tone: "ok" | "warn" | "info" | "mute"; title?: ReactNode; children?: ReactNode }) {
	const cls =
		tone === "ok"
			? "border-gms-ok/25 bg-gms-ok-soft text-gms-ok"
			: tone === "warn"
				? "border-gms-warn/25 bg-gms-warn-soft text-gms-warn"
				: tone === "info"
					? "border-gms-navy/15 bg-gms-navy-soft text-gms-navy-ink"
					: "border-gms-line bg-base-100 text-gms-ink-2";
	const Icon = tone === "ok" ? LockIcon : tone === "warn" ? AlertIcon : InfoIcon;
	return (
		<div role="status" className={`flex items-start gap-[0.625rem] rounded-box border px-[0.875rem] py-[0.625rem] text-[0.8125rem] ${cls}`}>
			<Icon className="mt-[0.125rem] size-[1rem] shrink-0" />
			<span>
				{title && <b className="font-semibold">{title}</b>}
				{title && children && <br />}
				{children && <span className={title ? "text-gms-ink-2" : undefined}>{children}</span>}
			</span>
		</div>
	);
}

/**
 * The banners above an editor, in the order that matters: the window (closed
 * / not open yet — for a NOC it is why nothing can be changed; for an admin a
 * reminder that NOCs cannot), editing on behalf. A submitted phase says so on
 * its poster and scoreboard, not here.
 */
export function EditorNotices({
	submission,
	window: w,
	canEdit,
	asAdmin,
	onBehalfOf,
}: {
	submission: EntrySubmission;
	window: EntryWindowStatus;
	canEdit: boolean;
	/** An admin (never window- or final-bound). */
	asAdmin: boolean;
	/** The organisation's name when the caller is not the NOC itself. */
	onBehalfOf: string | null;
}) {
	const { t } = useTranslation("entries");
	const final = submission.state === "FINAL";
	return (
		<>
			{!w.open && !final && (asAdmin ? (
				<Notice tone="mute">{t("window.adminNote")}</Notice>
			) : (
				<Notice tone="warn" title={t("window.closedTitle")}>
					{w.next_opens_at ? t("window.notOpenYet", { at: formatShort(w.next_opens_at) }) : t("window.closedBody")}
				</Notice>
			))}
			{!final && w.open === true && !canEdit && <Notice tone="mute">{t("page.readOnly")}</Notice>}
			{onBehalfOf && canEdit && <Notice tone="info">{t("page.onBehalf", { org: onBehalfOf })}</Notice>}
		</>
	);
}

/** The Statuses guide: the four states and the three window states, from the contract list and the page's pills. */
export function useEntriesGuide(): { title: string; groups: StatusGuideGroup[] } {
	const { t } = useTranslation("entries");
	return {
		title: t("guide.title"),
		groups: [
			{
				key: "states",
				heading: t("guide.states"),
				items: ENTRY_STATES.map((s) => ({ key: s, label: t(`state.${s}`), description: t(`state.hint.${s}`), marker: <StatePill state={s} /> })),
			},
			{
				key: "window",
				heading: t("guide.window"),
				note: t("guide.windowNote"),
				items: [
					{ key: "open", label: t("window.open"), description: t("guide.windowOpen"), marker: <WindowStatusPill state="open" /> },
					{ key: "upcoming", label: t("window.upcoming"), description: t("guide.windowUpcoming"), marker: <WindowStatusPill state="upcoming" /> },
					{ key: "closed", label: t("window.closed"), description: t("guide.windowClosed"), marker: <WindowStatusPill state="closed" /> },
				],
			},
		],
	};
}

/** "How sport entries work": Sector 1's two steps, the windows rule, the cascade. The Shortlist is Sector 2's (user, 2026-10-29). */
export function HowItWorksSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
	const { t } = useTranslation("entries");
	const steps = ["EOI", "EBN"] as const;
	return (
		<Modal open={open} onClose={onClose} title={t("help.title")}>
			<ol className="grid gap-[0.375rem] min-[30rem]:grid-cols-2">
				{steps.map((s, i) => (
					<li key={s} className="grid content-start gap-[0.25rem] rounded-field bg-gms-soft p-[0.625rem] text-[0.75rem] leading-snug text-gms-ink-2">
						<span aria-hidden className="grid size-[1.375rem] place-items-center rounded-full bg-gms-navy text-[0.71875rem] font-bold text-white">
							{i + 1}
						</span>
						<b className="text-[0.78125rem] text-gms-navy-ink">{t(`help.steps.${s}.title`)}</b>
						{t(`help.steps.${s}.body`)}
					</li>
				))}
			</ol>
			<p className="mt-[0.75rem] text-[0.8125rem] leading-relaxed text-gms-ink-2">{t("help.windows")}</p>
			<p className="mt-[0.5rem] text-[0.8125rem] leading-relaxed text-gms-ink-2">{t("help.remove")}</p>
		</Modal>
	);
}

/** SUPER_ADMIN: reopen a submitted phase with a reason (the audit headline). The Shortlist's ReopenSheet. */
export function ReopenSheet({
	open,
	phase,
	gamesId,
	organizationId,
	orgName,
	onClose,
}: {
	open: boolean;
	phase: EntryPhase;
	gamesId: string;
	organizationId: string;
	orgName: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("entries");
	const [reason, setReason] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		if (open) {
			setReason("");
			setError(null);
		}
	}, [open]);
	const phaseName = t(`phase.${phase}`);
	const submit = async () => {
		const trimmed = reason.trim();
		if (!trimmed) {
			setError(t("reopen.reasonRequired"));
			return;
		}
		setBusy(true);
		try {
			await reopenEntryPhase(phase, { games_id: gamesId, organization_id: organizationId, reason: trimmed });
			await invalidateEntries();
			toast.success(t("reopen.done", { phase: phaseName }));
			onClose();
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(false);
		}
	};
	return (
		<Modal
			open={open}
			onClose={onClose}
			dismissible={!busy}
			title={t("reopen.title", { phase: phaseName })}
			description={orgName}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line" onClick={onClose} disabled={busy}>
						{t("reopen.cancel")}
					</button>
					<button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("reopen.confirm")}
					</button>
				</ModalActions>
			}
		>
			<p className="mb-[0.75rem] text-[0.8125rem] text-gms-ink-2">
				{t("reopen.body")} {phase === "EOI" && t("reopen.eoiAlso")}
			</p>
			<label className="grid gap-[0.375rem] text-[0.8125rem] font-semibold text-gms-navy-ink">
				{t("reopen.reason")}
				<textarea
					className={`textarea w-full font-normal ${error ? "textarea-error" : ""}`}
					rows={3}
					maxLength={ENTRY_REASON_MAX}
					value={reason}
					onChange={(e) => {
						setReason(e.target.value);
						setError(null);
					}}
					aria-invalid={!!error}
				/>
			</label>
			{error && (
				<p role="alert" className="mt-[0.375rem] text-[0.75rem] text-gms-bad">
					{error}
				</p>
			)}
		</Modal>
	);
}

/**
 * Unsaved changes: "Discard your changes?" before leaving the editor — a link,
 * the page-bar Back / exit, browser Back, switching NOC or games. Refresh and
 * tab close go through `beforeunload` instead: there the browser shows its
 * OWN generic "Leave site?" dialog — no page can put its text in it (browsers
 * ignore custom messages), so that one cannot say "Discard your changes?".
 */
export function LeaveGuard({ dirtyCount }: { dirtyCount: number }) {
	const { t } = useTranslation("entries");
	const dirty = dirtyCount > 0;
	const blocker = useBlocker({
		shouldBlockFn: ({ current, next }) => dirty && leavesEditor(current, next),
		enableBeforeUnload: dirty,
		withResolver: true,
	});
	return (
		<Modal
			open={blocker.status === "blocked"}
			onClose={() => blocker.reset?.()}
			title={t("leave.title")}
			size="sm"
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line" onClick={() => blocker.reset?.()}>
						{t("leave.stay")}
					</button>
					<button type="button" className="btn btn-error" onClick={() => blocker.proceed?.()}>
						{t("leave.discard")}
					</button>
				</ModalActions>
			}
		>
			<p className="text-[0.875rem] text-gms-ink-2">{t("leave.body", { count: dirtyCount, n: dirtyCount })}</p>
		</Modal>
	);
}
